import { useState } from 'react';
import { Button, Input } from '@staff-app/ui';
import { toast } from 'sonner';
import { documentsApi, type CustomerDocument } from '@/features/customers/lib/documents-api';

/** The Stripe invoice a document is filed alongside, as stored in `meta`. */
const invoiceRef = (meta: Record<string, unknown>): { id: string; url?: string } | null => {
  const id = typeof meta?.invoiceId === 'string' ? meta.invoiceId : null;
  if (!id) return null;
  const url = typeof meta?.invoiceUrl === 'string' ? meta.invoiceUrl : undefined;
  return { id, url };
};

/** Stripe invoice ids. Loose on the suffix, strict on the prefix — Stripe owns the format. */
const INVOICE_ID = /^in_[A-Za-z0-9]+$/;

/**
 * The Invoice ID cell: shows the cross-reference, and lets an operator attach one by hand.
 *
 * ONE component for both surfaces — the Document Center's search results and a customer's own
 * Documents page — because they are the same job and the rules are easy to get subtly wrong.
 * Two copies would drift, and the way they would drift is silent: one page validating the id
 * and the other not.
 *
 * Normally the link is written by the Billing Console when its create succeeds. Three ordinary
 * things leave it blank — the document was uploaded outside a billing card, the action raised no
 * invoice (the next-invoice proration path), or the best-effort PATCH failed silently — and none
 * of them could be repaired from the UI before this.
 *
 * No `invoiceUrl` is written by hand: the Stripe dashboard base differs between test and live
 * mode and the browser cannot know which mode an id belongs to. A hand-linked id therefore
 * renders as plain text rather than a hyperlink, which is honest — better than a URL that 404s.
 * Server-written links keep theirs.
 */
export function InvoiceLinkCell({
  doc,
  prefix,
  onSaved,
}: {
  doc: CustomerDocument;
  /** testid namespace: `doc-center` → `doc-center-link-<id>`, `document` → `document-link-<id>`. */
  prefix: string;
  /** Re-read the list, so the cell shows what is STORED rather than what was typed. */
  onSaved: () => void | Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const inv = invoiceRef(doc.meta);
  if (inv) {
    return inv.url ? (
      <a
        href={inv.url}
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-4"
        data-testid={`${prefix}-invoice-${doc.id}`}
      >
        {inv.id}
      </a>
    ) : (
      <span data-testid={`${prefix}-invoice-${doc.id}`}>{inv.id}</span>
    );
  }

  const save = async () => {
    const id = value.trim();
    if (!INVOICE_ID.test(id)) {
      toast.error('That does not look like a Stripe invoice id', {
        description: 'Expected the in_… form shown on the invoice itself.',
      });
      return;
    }
    setBusy(true);
    try {
      // Only the key being set. The server merge-patches `meta`, so there is nothing to read
      // first — and nothing to clobber: sending only our own key means a concurrent writer
      // stamping a different one cannot be overwritten by our stale copy of the row.
      await documentsApi.patch(doc.id, { meta: { invoiceId: id } } as never);
      toast.success('Invoice linked', { description: id });
      setEditing(false);
      setValue('');
      await onSaved();
    } catch (e: unknown) {
      toast.error('Could not link the invoice', {
        description: (e as Error)?.message ?? 'Unknown error',
      });
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        className="text-muted-foreground underline underline-offset-4"
        onClick={() => {
          setValue('');
          setEditing(true);
        }}
        data-testid={`${prefix}-link-${doc.id}`}
      >
        {'Link invoice'}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void save();
          if (e.key === 'Escape') setEditing(false);
        }}
        placeholder="in_…"
        className="h-7 w-40"
        data-testid={`${prefix}-link-input-${doc.id}`}
      />
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={() => void save()}
        data-testid={`${prefix}-link-save-${doc.id}`}
      >
        {'Save'}
      </Button>
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditing(false)}>
        {'Cancel'}
      </Button>
    </div>
  );
}
